import { General } from "../../siteManagement/general";
import { SheetEventType } from "../../siteManagement/sheet";
import { Dialog } from "../core/components/dialog";
import { FieldInstance } from "../core/components/field";
import { Grid } from "../core/components/grid";
import { TextBox } from "../core/components/textBox";
import { GridCustomToolBarButton, ToolBarInGridButtonType } from "../core/components/toolBar";
import { Config } from "../core/config/config";
import { DialogHelper } from "../core/util/dialogHelper";
import { Helper } from "../core/util/helper";
import { HTTPService, JsonResultClass } from "../core/util/httpManager";
import { NotificationHelper } from "../core/util/notificationHelper";
import React from "react";

const ProjectResourceGrid = ({
    entity,
    gridOptions
}) => {
    const selectedItem = React.useRef<ProjectResource>({});
    const newItem = React.useRef<ProjectResource>({});

    const [timestamp, setTimestamp] = React.useState(Date.now);
    const [data, setData] = React.useState<ProjectResource[]>([]);
    const [newDialogOpen, setNewDialogOpen] = React.useState(false);
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);

    React.useEffect(() => {
        const loadData = async () => {
            const result = await HTTPService.get<JsonResultClass<ProjectResource[]>>("ProjectResource/Get?projNr=" + encodeURIComponent(entity.Projnr));
            if (result.Code == 0) {
                setData(result.Data);
            } else {
                NotificationHelper.showErrorMessage(result.Message);
            }
        }

        loadData();
    }, [timestamp])

    const create = async (data) => {
        const result = await HTTPService.post<JsonResultClass>("ProjectResource/New", data);
        if (result.Code == 0) {
            setTimestamp(Date.now);
        } else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    const add = async (data) => {
        const result = await HTTPService.post<JsonResultClass>("ProjectResource/Add", data);
        if (result.Code == 0) {
            setTimestamp(Date.now);
        } else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    const save = async (data) => {
        const result = await HTTPService.post<JsonResultClass>("ProjectResource/Save", data);
        if (result.Code == 0) {
            setTimestamp(Date.now);
            NotificationHelper.showSuccessMessage(Config.translations.savingSuccessfulMessage);
        } else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    const remove = async (isGuid: string) => {
        const result = await HTTPService.post<JsonResultClass>("ProjectResource/Delete?isGuid=" + encodeURIComponent(isGuid));
        if (result.Code == 0) {
            setTimestamp(Date.now);
        } else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    const buttons: Array<GridCustomToolBarButton> = [
        {
            id: "new",
            caption: Config.translations.newLabel,
            enabled: entity.RecordRight.CanWrite,
            iconClass: "k-i-plus",
            autoEnabledOnRecordSelected: false
        },
        {
            id: "add",
            caption: Config.translations.add,
            enabled: entity.RecordRight.CanWrite,
            iconClass: "k-i-seek-s",
            autoEnabledOnRecordSelected: false
        },
        {
            id: "edit",
            caption: Config.translations.editLabel,
            enabled: entity.RecordRight.CanWrite,
            iconClass: "k-i-close",
            autoEnabledOnRecordSelected: true
        },
        {
            id: "delete",
            caption: Config.translations.deleteLabel,
            enabled: entity.RecordRight.CanWrite,
            iconClass: "k-i-close",
            autoEnabledOnRecordSelected: true
        }
    ];

    const onButtonClick = async (id: string) => {
        switch (id) {
            case "new":
                newItem.current = {
                    ProjNr: entity.Projnr,
                    Intern: 0
                };
                setNewDialogOpen(true);
                break;
            case "add":
                openResourceSearch();
                break;
            case "edit":
                setEditDialogOpen(true);
                break;
            case "delete":
                let isOk = await DialogHelper.confirm(Helper.getTranslation("Standard.Msg.Loeschen"));
                if (isOk)
                    remove(selectedItem.current.IsGuid);
                break;
        }
    }

    const openResourceSearch = () => {
        const sheet = General.Sheet.Manager.createSheetWithUrl(`DynamicSearch/Window/Resource_Project?multi=true&displayPattern=[[name]]&cus_projnr=${encodeURIComponent(entity.Projnr)}`, null, { disableTabTitle: true });
        sheet.bind(SheetEventType.ItemsSelected, async (data) => {
            let vms = [];
            for (let item of data.data) {
                vms.push({
                    ProjNr: entity.Projnr,
                    RessNr: item.ressnr,
                    Intern: item.intern
                })
            }
            add(vms);
        });
    }

    return (<>
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Grid id="projectResourceGrid"
                data={data}
                options={gridOptions}
                onSelectionChanged={(dataItem) => {
                    selectedItem.current = dataItem;
                }}
                customButtons={buttons}
                onClickCustomButton={onButtonClick}
            />
        </div>
        <Dialog title={Helper.getTranslation("Standard.Neu")}
            actions={[
                {
                    label: Config.translations.cancel,
                    action: () => { }
                },
                {
                    label: Config.translations.ok,
                    action: () => {
                        create(newItem.current);
                    }
                }
            ]}
            afterClose={() => setNewDialogOpen(false)}
            open={newDialogOpen}>
            <div>
                <FieldInstance label={Helper.getTranslation("Field.Ansprechpartner.ansprechpartner")}>
                    <TextBox field={{ model: "", type: "" }} value={newItem.current?.Name} onChange={(_, value) => newItem.current.Name = value} />
                </FieldInstance>
                <FieldInstance label={Helper.getTranslation("Field.Ansprechpartner.vorname")}>
                    <TextBox field={{ model: "", type: "" }} value={newItem.current?.Vorname} onChange={(_, value) => newItem.current.Vorname = value} />
                </FieldInstance>
                <FieldInstance label={Helper.getTranslation("Ressource.Fremdfirma")}>
                    <TextBox field={{ model: "", type: "" }} value={newItem.current?.FremdFirma} onChange={(_, value) => newItem.current.FremdFirma = value} />
                </FieldInstance>
            </div>
        </Dialog>
        <Dialog title={Helper.getTranslation("Standard.Bearbeiten")}
            actions={[
                {
                    label: Config.translations.cancel,
                    action: () => { }
                },
                {
                    label: Config.translations.ok,
                    action: () => {
                        save(selectedItem.current);
                    }
                }
            ]}
            afterClose={() => setEditDialogOpen(false)}
            open={editDialogOpen}>
            <div>
                <FieldInstance label={Helper.getTranslation("Field.Ansprechpartner.ansprechpartner")}>
                    <TextBox field={{ model: "", type: "" }} value={selectedItem.current?.Name} onChange={(_, value) => selectedItem.current.Name = value} />
                </FieldInstance>
                <FieldInstance label={Helper.getTranslation("Field.Ansprechpartner.vorname")}>
                    <TextBox field={{ model: "", type: "" }} value={selectedItem.current?.Vorname} onChange={(_, value) => selectedItem.current.Vorname = value} />
                </FieldInstance>
                <FieldInstance label={Helper.getTranslation("Ressource.Fremdfirma")}>
                    <TextBox field={{ model: "", type: "" }} value={selectedItem.current?.FremdFirma} onChange={(_, value) => selectedItem.current.FremdFirma = value} />
                </FieldInstance>
            </div>
        </Dialog>
    </>
    );
}

export interface ProjectResource {
    ProjNr?: string;
    RessNr?: string;
    IsGuid?: string;
    Name?: string;
    Vorname?: string;
    Intern?: number;
    FremdFirma?: string;
    RessTyp?: number;
    RessTypName?: string;
    OgrNr?: number;
}

export default ProjectResourceGrid;